import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { Loader2, LogIn, LogOut, ShieldAlert, ShieldCheck, Star } from 'lucide-react';
import {
  onAdminAuthStateChanged,
  signInAdmin,
  signOutAdmin,
  subscribeResponses,
  type StoredResponse,
} from './firebase';

const ADMIN_EMAIL_DOMAIN = 'esprot.co.jp';
const isAdminEmail = (email: string | null | undefined): boolean =>
  !!email && email.toLowerCase().endsWith(`@${ADMIN_EMAIL_DOMAIN}`);

type AuthState = 'loading' | 'signed-out' | 'unauthorized' | 'authorized';

export default function Admin() {
  const [user, setUser] = useState<User | null>(null);
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    return onAdminAuthStateChanged((u) => {
      setUser(u);
      if (!u) setAuthState('signed-out');
      else if (isAdminEmail(u.email)) setAuthState('authorized');
      else setAuthState('unauthorized');
    });
  }, []);

  const handleSignIn = async () => {
    setSignInError(null);
    setSubmitting(true);
    try {
      await signInAdmin();
    } catch (err) {
      setSignInError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="max-w-4xl mx-auto p-6">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-indigo-600" size={28} />
            <h1 className="text-xl font-bold">アンケート管理</h1>
          </div>
          {user && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 truncate max-w-[200px]">{user.email}</span>
              <button
                onClick={() => void signOutAdmin()}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded-lg bg-white"
              >
                <LogOut size={14} />
                サインアウト
              </button>
            </div>
          )}
        </header>

        {authState === 'loading' && (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <Loader2 className="animate-spin mr-2" size={20} />
            読み込み中…
          </div>
        )}

        {authState === 'signed-out' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-md mx-auto">
            <ShieldCheck className="mx-auto text-indigo-500 mb-3" size={40} />
            <h2 className="font-bold text-lg mb-2">管理者サインイン</h2>
            <p className="text-gray-600 text-sm mb-5">
              <span className="font-mono text-xs">@{ADMIN_EMAIL_DOMAIN}</span>
              {' '}のGoogleアカウントでサインインしてください。
            </p>
            <button
              onClick={handleSignIn}
              disabled={submitting}
              className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-medium text-white shadow-sm transition ${
                submitting
                  ? 'bg-indigo-400 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98]'
              }`}
            >
              {submitting ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <LogIn size={16} />
              )}
              Googleでサインイン
            </button>
            {signInError && (
              <p className="text-red-600 text-sm mt-4 break-words">{signInError}</p>
            )}
          </div>
        )}

        {authState === 'unauthorized' && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <ShieldAlert className="mx-auto text-red-500 mb-3" size={40} />
            <h2 className="font-bold text-lg mb-2">アクセス権限がありません</h2>
            <p className="text-gray-600 text-sm mb-4">
              このアカウント (<span className="font-mono text-xs">{user?.email}</span>) は
              管理者として登録されていません。
            </p>
            <button
              onClick={() => void signOutAdmin()}
              className="text-sm text-indigo-600 hover:text-indigo-800 underline"
            >
              別のアカウントでサインイン
            </button>
          </div>
        )}

        {authState === 'authorized' && <AuthorizedDashboard />}
      </div>
    </div>
  );
}

function AuthorizedDashboard() {
  const [responses, setResponses] = useState<StoredResponse[] | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    setFetchError(null);
    return subscribeResponses(
      (r) => {
        setResponses(r);
        setFetchError(null);
      },
      (err) => setFetchError(err.message),
    );
  }, []);

  if (fetchError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-6">
        <p className="font-bold mb-2">取得に失敗しました</p>
        <p className="text-sm break-words">{fetchError}</p>
      </div>
    );
  }

  if (responses === null) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="animate-spin mr-2" size={20} />
        読み込み中…
      </div>
    );
  }

  const list = responses;
  const total = list.length;
  const avg = total ? list.reduce((a, r) => a + r.rating, 0) / total : 0;
  const median = total ? computeMedian(list.map((r) => r.rating)) : 0;
  const highRate = total ? (list.filter((r) => r.rating >= 4).length / total) * 100 : 0;

  return (
    <section className="space-y-6">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        リアルタイム更新中
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="回答数" value={total.toString()} />
        <StatCard label="平均評価" value={total ? avg.toFixed(2) : '—'} suffix={total ? ' / 5' : undefined} />
        <StatCard label="中央値" value={total ? median.toString() : '—'} suffix={total ? ' / 5' : undefined} />
        <StatCard label="高評価率 (★4以上)" value={total ? `${Math.round(highRate)}%` : '—'} />
      </div>

      {total > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RatingDistribution responses={list} />
          <DailyTrend responses={list} />
        </div>
      )}

      {total === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-500">
          まだ回答はありません。新しい回答が届くと自動で表示されます。
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm">
          <h3 className="text-sm font-bold text-gray-700 px-4 pt-4 pb-2">回答一覧</h3>
          <ul className="divide-y divide-gray-100">
            {list.map((r) => (
              <li key={r.id} className="p-4 flex gap-4 items-start">
                <div className="flex shrink-0 gap-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      size={18}
                      className={
                        s <= r.rating
                          ? 'fill-orange-400 text-orange-400'
                          : 'text-gray-200 fill-gray-100'
                      }
                    />
                  ))}
                </div>
                <div className="flex-1 min-w-0">
                  {r.comment ? (
                    <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{r.comment}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">(コメントなし)</p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    {r.createdAt ? r.createdAt.toLocaleString('ja-JP') : '（時刻不明）'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function RatingDistribution({ responses }: { responses: StoredResponse[] }) {
  const counts = [1, 2, 3, 4, 5].map(
    (rating) => responses.filter((r) => r.rating === rating).length,
  );
  const max = Math.max(...counts, 1);

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="text-sm font-bold text-gray-700 mb-4">評価分布</h3>
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = counts[rating - 1];
          const pct = (count / max) * 100;
          const ratio = responses.length ? (count / responses.length) * 100 : 0;
          return (
            <div key={rating} className="flex items-center gap-3 text-xs">
              <div className="flex shrink-0 w-20 gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    size={12}
                    className={
                      s <= rating
                        ? 'fill-orange-400 text-orange-400'
                        : 'text-gray-200 fill-gray-100'
                    }
                  />
                ))}
              </div>
              <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="shrink-0 w-20 text-right text-gray-600">
                <span className="font-bold text-gray-800">{count}</span>
                <span className="text-gray-400"> ({ratio.toFixed(0)}%)</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DailyTrend({ responses }: { responses: StoredResponse[] }) {
  const days = 14;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const buckets: { date: Date; count: number; sumRating: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    buckets.push({ date, count: 0, sumRating: 0 });
  }

  responses.forEach((r) => {
    if (!r.createdAt) return;
    const d = new Date(
      r.createdAt.getFullYear(),
      r.createdAt.getMonth(),
      r.createdAt.getDate(),
    );
    const bucket = buckets.find((b) => b.date.getTime() === d.getTime());
    if (bucket) {
      bucket.count += 1;
      bucket.sumRating += r.rating;
    }
  });

  const maxCount = Math.max(...buckets.map((b) => b.count), 1);

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h3 className="text-sm font-bold text-gray-700 mb-4">日別回答数 (直近{days}日)</h3>
      <div className="flex items-end gap-1 h-32">
        {buckets.map((b, i) => {
          const height = (b.count / maxCount) * 100;
          const avgR = b.count ? b.sumRating / b.count : 0;
          return (
            <div
              key={i}
              className="flex-1 flex flex-col items-center gap-1 group relative"
              title={`${b.date.toLocaleDateString('ja-JP')}: ${b.count}件${b.count ? ` / 平均★${avgR.toFixed(1)}` : ''}`}
            >
              <div className="text-[10px] text-gray-500 h-4">
                {b.count > 0 ? b.count : ''}
              </div>
              <div className="flex-1 w-full flex items-end">
                <div
                  className={`w-full rounded-t transition-all duration-500 ${
                    b.count === 0
                      ? 'bg-gray-100'
                      : avgR >= 4
                        ? 'bg-gradient-to-t from-emerald-500 to-emerald-400'
                        : avgR >= 3
                          ? 'bg-gradient-to-t from-amber-500 to-amber-400'
                          : 'bg-gradient-to-t from-rose-500 to-rose-400'
                  }`}
                  style={{ height: `${Math.max(height, b.count > 0 ? 4 : 2)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-2">
        <span>{buckets[0].date.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}</span>
        <span>今日</span>
      </div>
    </div>
  );
}

function computeMedian(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function StatCard({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-800">
        {value}
        {suffix && <span className="text-sm font-normal text-gray-500">{suffix}</span>}
      </p>
    </div>
  );
}
